"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.myWeekSchema = exports.myWeekLineItemSchema = exports.myWeekDaySchema = exports.myWeekDayItemSchema = void 0;
const zod_1 = require("zod");
const allocation_1 = require("../delivery/allocation");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 16 Oct 2026 · My week — the caller's plan, day by day and line by line.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The reworked homescreen mockup draws the caller's allocations twice: Kezdőlap's *Allokációim*
 * (a column per day, a row per line item, a tooltip on each) and Időrögzítés's *A hét
 * összesítése* (the week per line item against capacity). One read serves both.
 *
 * ⚠️⚠️ **THE RESOURCE GRID'S OWN ROW, NOT A SECOND IMPLEMENTATION.** Capacity, the allocated hours
 * and the overtime rate are `ResourceService.compute` narrowed to the caller, so this week can
 * never disagree with the Erőforrás dashboard. What is added here is only what the grid has no
 * column for: what was LOGGED against each line item on each day, and the NAME of whatever took
 * a day away (a holiday, a leave type).
 *
 * ⚠️ **A line item, not an allocation, is the row.** Two bookings on one deliverable in one week
 * read as one line with their hours together — the mockup groups by `a.li` — and the tooltip
 * says how many there were instead of printing one window that is only half the truth.
 *
 * ⚠️ Every figure is a STRING, like every other `numeric` on the wire.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** What both renderings say about a line item, before any day is chosen. */
const myWeekLineItemBase = {
    lineItemId: zod_1.z.string(),
    lineItemName: zod_1.z.string(),
    /** R-57 · the public addresses a click opens. Null when the reader cannot see the contract. */
    lineItemIdentifier: zod_1.z.string().nullable(),
    contractIdentifier: zod_1.z.string().nullable(),
    contractCode: zod_1.z.string().nullable(),
    projectName: zod_1.z.string().nullable(),
    projectColor: zod_1.z.string().nullable(),
    /** The overtime type planned on it, and its rate; null on an ordinary booking. */
    overtimeTypeName: zod_1.z.string().nullable(),
    multiplier: zod_1.z.string(),
};
exports.myWeekDayItemSchema = zod_1.z.object({
    ...myWeekLineItemBase,
    /** Planned on THIS day. */
    hours: zod_1.z.string(),
    /** `hours` × the overtime rate — the *elszámolt* beside a booking at a premium. */
    billedHours: zod_1.z.string(),
    /** Logged on this day against the line item's tasks. */
    loggedHours: zod_1.z.string(),
    /** Planned across the whole week on this line item. */
    weekHours: zod_1.z.string(),
    /** How many allocations put hours on this day. The window below is null when it is not one. */
    allocationCount: zod_1.z.number().int().positive(),
    fromDate: wireDateSchema.nullable(),
    toDate: wireDateSchema.nullable(),
    distribution: zod_1.z.enum(allocation_1.ALLOCATION_DISTRIBUTIONS).nullable(),
});
exports.myWeekDaySchema = zod_1.z.object({
    date: wireDateSchema,
    capacity: zod_1.z.string(),
    allocated: zod_1.z.string(),
    /** Saturday or Sunday — drawn only when the calendar makes it a working day. */
    weekend: zod_1.z.boolean(),
    /** The kernel's: the schedule and the calendar together. */
    workingDay: zod_1.z.boolean(),
    /** The holiday that REMOVED the day, if one did. An extra working Saturday is not here. */
    holidayName: zod_1.z.string().nullable(),
    /** The approved absence covering the day, by its type's name. */
    leaveTypeName: zod_1.z.string().nullable(),
    items: zod_1.z.array(exports.myWeekDayItemSchema),
});
exports.myWeekLineItemSchema = zod_1.z.object({
    ...myWeekLineItemBase,
    /** Planned across the week. */
    hours: zod_1.z.string(),
    billedHours: zod_1.z.string(),
});
exports.myWeekSchema = zod_1.z.object({
    from: wireDateSchema,
    to: wireDateSchema,
    /**
     * ⚠️ **Whether the workspace plans this person at all** (`members.resource_planned`). Kezdőlap
     * hides *Allokációim* for somebody it does not — an empty card would ask them about a plan
     * nobody makes — while Időrögzítés still says *nincs allokációd* for the week.
     */
    planned: zod_1.z.boolean(),
    allocated: zod_1.z.string(),
    capacity: zod_1.z.string(),
    days: zod_1.z.array(exports.myWeekDaySchema),
    /** The week per line item, most hours first — *A hét összesítése*. */
    lineItems: zod_1.z.array(exports.myWeekLineItemSchema),
});
//# sourceMappingURL=my-week.js.map